import type { ReactNode } from 'react'
import type { SetClientOrganizerInvoice } from '@st/sdk'
import { match } from '@st/pattern'
import { TextInput, ToggleSwitch } from '@st/theme'
import { urlScheme } from '@st/util/url'

/** The firm's unsaved invoice link and whether the client must pay before signing. */
export type InvoiceLinkDraft = { url: string; required: boolean }

/**
 * Mirrors the backend's WebUrl: the client opens this link, so only HTTPS
 * pages qualify. Pasted links often carry surrounding whitespace that the
 * backend's URI parsing rejects, so drafts are checked and saved trimmed.
 */
export function isValidInvoiceUrl(url: string) {
  return urlScheme(url.trim()) == 'https'
}

/** Saves the draft as the client's View invoice step, or removes it when null. */
export function setInvoiceRequest(
  folderId: string,
  draft: InvoiceLinkDraft | null
): SetClientOrganizerInvoice {
  return match(draft)
    .returnType<SetClientOrganizerInvoice>()
    .with(null, () => ({ type: 'folders/setClientOrganizerInvoice', folderId, requiredBy: [] }))
    .otherwise(({ url, required }) => ({
      type: 'folders/setClientOrganizerInvoice',
      folderId,
      paymentMethod: { type: 'payment_link', url: url.trim() },
      requiredBy: match(required)
        .with(true, () => ['tax_return_delivery' as const])
        .with(false, () => [])
        .exhaustive()
    }))
}

/** The link field and payment requirement; `children` sits beside the link. */
export function InvoiceLinkFields({
  draft,
  onEdit,
  children
}: {
  draft: InvoiceLinkDraft
  onEdit: (edit: { url: string } | { required: boolean }) => void
  children?: ReactNode
}) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <label className="font-semibold">Invoice link</label>
        <div className="flex items-center gap-2">
          <TextInput
            className="w-full"
            ariaLabel="Invoice link"
            placeholder="https://"
            value={draft.url}
            autoFocus
            onChange={(url) => onEdit({ url })}
          />
          {children}
        </div>
      </div>
      <div className="flex items-center justify-between gap-4">
        <span className="text-sm text-gray-700">
          Require payment before client can sign their return
        </span>
        <ToggleSwitch
          aria-label="Require payment before client can sign their return"
          value={draft.required}
          onChange={(required) => onEdit({ required })}
        />
      </div>
    </div>
  )
}
